import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Empty,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Row,
  Select,
  Skeleton,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  AuditOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  FileTextOutlined,
  LogoutOutlined,
  PlusCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getMonthlyInvoices } from "../../services/usageBillingService";
import { getAuditLogs } from "../../services/adminService";
import { createTicket, getMyTickets } from "../../services/supportService";

const { Header, Sider, Content } = Layout;
const { Title, Text }            = Typography;

const toArray = (d) => Array.isArray(d) ? d : (d?.results ?? []);

const INVOICE_STATUS_COLOR = {
  PENDING:   "orange",
  PAID:      "green",
  OVERDUE:   "red",
  CANCELLED: "default",
};

const MENU_ITEMS = [
  { key: "dashboard", icon: <DashboardOutlined />,       label: "Dashboard"      },
  { key: "invoices",  icon: <FileTextOutlined />,        label: "Invoices"       },
  { key: "support",   icon: <CustomerServiceOutlined />, label: "Support Tickets"},
  { key: "audit",     icon: <AuditOutlined />,           label: "Audit Logs"     },
  { key: "profile",   icon: <UserOutlined />,            label: "My Profile"     },
];

export default function BillingDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [collapsed,    setCollapsed]    = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("dashboard");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [retryCount,   setRetryCount]   = useState(0);

  // Invoices
  const [invoices,       setInvoices]       = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);

  // Audit logs
  const [auditLogs,    setAuditLogs]    = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLoaded,  setAuditLoaded]  = useState(false);

  // Support
  const [myTickets,      setMyTickets]      = useState([]);
  const [ticketModal,    setTicketModal]    = useState(false);
  const [ticketDetail,   setTicketDetail]   = useState(null);
  const [supportLoaded,  setSupportLoaded]  = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);
  const [ticketForm]                        = Form.useForm();

  // ── Data fetchers ──────────────────────────────────────────────────────────

  const loadInvoices = useCallback(async () => {
    setInvoiceLoading(true);
    try {
      const res = await getMonthlyInvoices();
      setInvoices(toArray(res.data).map(r => ({ ...r, _rowKey: r.id })));
      setInvoicesLoaded(true);
    } catch {
      message.error("Failed to load invoices.");
    } finally {
      setInvoiceLoading(false);
    }
  }, []);

  const loadAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await getAuditLogs();
      setAuditLogs(toArray(res.data).map(r => ({ ...r, _rowKey: r.id })));
      setAuditLoaded(true);
    } catch {
      message.error("Failed to load audit logs.");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const loadSupport = useCallback(async () => {
    setSupportLoading(true);
    try {
      const res = await getMyTickets();
      setMyTickets(toArray(res.data).map(r => ({ ...r, key: r.id })));
      setSupportLoaded(true);
    } catch { /* silent */ } finally { setSupportLoading(false); }
  }, []);

  // Initial load — invoices for dashboard home
  useEffect(() => {
    setLoading(true);
    setError(null);
    loadInvoices()
      .catch(() => setError("Failed to load invoice data. Please refresh."))
      .finally(() => setLoading(false));
  }, [retryCount, loadInvoices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy load on menu switch
  useEffect(() => {
    if (selectedMenu === "invoices" && !invoicesLoaded) loadInvoices();
    if (selectedMenu === "audit"    && !auditLoaded)    loadAuditLogs();
    if (selectedMenu === "support"  && !supportLoaded)  loadSupport();
  }, [selectedMenu, invoicesLoaded, auditLoaded, supportLoaded, loadInvoices, loadAuditLogs, loadSupport]);

  // ── Derived stats ──────────────────────────────────────────────────────────

  const stats = {
    total:   invoices.length,
    pending: invoices.filter(i => i.status === "PENDING").length,
    paid:    invoices.filter(i => i.status === "PAID").length,
    overdue: invoices.filter(i => i.status === "OVERDUE").length,
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleLogout = () => { logout(); navigate("/login"); };

  const handleCreateTicket = async (values) => {
    try {
      await createTicket(values);
      message.success("Ticket submitted successfully.");
      setTicketModal(false);
      ticketForm.resetFields();
      loadSupport();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to submit ticket.");
    }
  };

  // ── Column definitions ────────────────────────────────────────────────────

  const invoiceCols = [
    {
      title: "Invoice ID", key: "id", width: 110,
      render: (_, r) => (
        <Text code style={{ fontSize: 11 }}>
          {String(r.id).slice(0, 8).toUpperCase()}
        </Text>
      ),
    },
    { title: "Month",    dataIndex: "billing_month",  key: "billing_month",  width: 110 },
    { title: "Entity",   dataIndex: "entity_name",    key: "entity_name",    ellipsis: true },
    {
      title: "Amount", dataIndex: "total_amount", key: "total_amount", width: 120,
      render: v => <Text strong>${parseFloat(v ?? 0).toFixed(2)}</Text>,
    },
    {
      title: "Status", dataIndex: "status", key: "status", width: 110,
      render: v => <Tag color={INVOICE_STATUS_COLOR[v] ?? "default"}>{v}</Tag>,
    },
    {
      title: "Due Date", dataIndex: "due_date", key: "due_date", width: 115,
      render: d => d ? new Date(d).toLocaleDateString() : "—",
    },
  ];

  const auditCols = [
    {
      title: "Time", dataIndex: "timestamp", key: "timestamp", width: 160,
      render: d => d ? new Date(d).toLocaleString() : "—",
    },
    { title: "User",   dataIndex: "user_email",  key: "user_email",  ellipsis: true },
    { title: "Action", dataIndex: "action",      key: "action",      width: 160,
      render: v => <Tag>{v}</Tag> },
    { title: "Resource", dataIndex: "resource_type", key: "resource_type", width: 130 },
    { title: "Details",  dataIndex: "details",       key: "details",       ellipsis: true,
      render: v => typeof v === "object" ? JSON.stringify(v) : (v ?? "—") },
  ];

  // ── Views ─────────────────────────────────────────────────────────────────

  function renderContent() {
    switch (selectedMenu) {

      // ── Dashboard home ─────────────────────────────────────────────────────
      case "dashboard":
        return (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {[
                { title: "Total Invoices",   value: stats.total,   color: "#1677ff" },
                { title: "Pending",          value: stats.pending, color: "#fa8c16" },
                { title: "Paid",             value: stats.paid,    color: "#52c41a" },
                { title: "Overdue",          value: stats.overdue, color: "#f5222d" },
              ].map(({ title, value, color }) => (
                <Col xs={24} sm={12} lg={6} key={title}>
                  <Card variant="borderless" style={{ borderRadius: 8 }}>
                    <Statistic
                      title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>{title}</Text>}
                      value={value}
                      styles={{ content: { color, fontSize: 28, fontWeight: 600 } }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>

            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={<Text strong>Recent Invoices</Text>}
              extra={
                <Button size="small" onClick={() => setSelectedMenu("invoices")}>
                  View All
                </Button>
              }
            >
              <Table
                rowKey="_rowKey"
                columns={invoiceCols}
                dataSource={invoices.slice(0, 10)}
                pagination={false}
                size="small"
                scroll={{ x: 800 }}
                locale={{ emptyText: <Empty description="No invoices found" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              />
            </Card>
          </>
        );

      // ── Invoices ───────────────────────────────────────────────────────────
      case "invoices":
        return (
          <Spin spinning={invoiceLoading}>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Invoices</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Table
                rowKey="_rowKey"
                columns={invoiceCols}
                dataSource={invoices}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                size="small"
                scroll={{ x: 800 }}
                locale={{ emptyText: <Empty description="No invoices found" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              />
            </Card>
          </Spin>
        );

      // ── Support Tickets ────────────────────────────────────────────────────
      case "support":
        return (
          <Spin spinning={supportLoading}>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Support Tickets</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}
              extra={
                <Button icon={<PlusCircleOutlined />} type="primary" size="small"
                  onClick={() => setTicketModal(true)}>
                  Report an Issue
                </Button>
              }
            >
              <Table
                size="small"
                dataSource={myTickets}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                locale={{ emptyText: "No tickets yet" }}
                columns={[
                  { title: "ID",       dataIndex: "id",       key: "id",       width: 90,
                    render: id => <Text code style={{ fontSize: 11 }}>{String(id).slice(0, 8)}</Text> },
                  { title: "Subject",  dataIndex: "subject",  key: "subject",  ellipsis: true },
                  { title: "Priority", dataIndex: "priority", key: "priority", width: 100,
                    render: p => <Tag color={{ LOW: "default", MEDIUM: "blue", HIGH: "orange", CRITICAL: "red" }[p] ?? "default"}>{p}</Tag> },
                  { title: "Status",   dataIndex: "status",   key: "status",   width: 120,
                    render: s => <Tag color={{ OPEN: "blue", IN_PROGRESS: "orange", RESOLVED: "green", CLOSED: "default" }[s] ?? "default"}>{s?.replace(/_/g, " ")}</Tag> },
                  { title: "Created",  dataIndex: "created_at", key: "created_at", width: 110,
                    render: d => d ? new Date(d).toLocaleDateString() : "—" },
                  { title: "Actions",  key: "actions", width: 80,
                    render: (_, r) => <Button size="small" onClick={() => setTicketDetail(r)}>View</Button> },
                ]}
              />
            </Card>
          </Spin>
        );

      // ── Audit Logs ─────────────────────────────────────────────────────────
      case "audit":
        return (
          <Spin spinning={auditLoading}>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Audit Logs</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Table
                rowKey="_rowKey"
                columns={auditCols}
                dataSource={auditLogs}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                size="small"
                scroll={{ x: 900 }}
                locale={{ emptyText: <Empty description="No audit logs found" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              />
            </Card>
          </Spin>
        );

      // ── My Profile ─────────────────────────────────────────────────────────
      case "profile":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>My Profile</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Email">{user?.email ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Name">
                  {[user?.first_name, user?.last_name].filter(Boolean).join(" ") || "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Role">
                  <Tag color="blue">BILLING_STAFF</Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>
            <ChangePasswordForm />
          </>
        );

      default:
        return null;
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const userMenuItems = [
    {
      key: "logout", icon: <LogoutOutlined />, label: "Logout",
      danger: true, onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={210}
        style={{ background: "#fff", borderRight: "1px solid #f0f0f0" }}
      >
        <div style={{
          height: 64, display: "flex", alignItems: "center", justifyContent: "center",
          borderBottom: "1px solid #f0f0f0", padding: "0 12px",
        }}>
          {!collapsed && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
              <Text strong style={{ color: "#1677ff", fontSize: 13, whiteSpace: "nowrap" }}>Digital Healthcare Network</Text>
              <Text style={{ color: "#1677ff", fontSize: 12, whiteSpace: "nowrap" }}>Billing</Text>
            </div>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedMenu]}
          onClick={({ key }) => setSelectedMenu(key)}
          style={{ border: "none", marginTop: 8 }}
          items={MENU_ITEMS}
        />
      </Sider>

      <Layout>
        <Header style={{
          background: "#fff", padding: "0 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid #f0f0f0",
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <div>
            <Text strong style={{ fontSize: 16, color: "#1677ff" }}>MCHUSA</Text>
            <Text style={{ fontSize: 14, color: "#8c8c8c", marginLeft: 8 }}>
              | Billing Staff
            </Text>
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
            <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <Avatar size={32} icon={<UserOutlined />} style={{ background: "#1677ff" }} />
              <Text style={{ fontSize: 14, color: "#262626" }}>
                {user?.email ?? "Billing Staff"}
              </Text>
            </div>
          </Dropdown>
        </Header>

        <Content style={{ padding: 24, background: "#f5f7fa" }}>
          {loading ? (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {[1, 2, 3].map(i => (
                  <Col xs={24} sm={12} lg={8} key={i}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </Card>
                  </Col>
                ))}
              </Row>
              <Card variant="borderless" style={{ borderRadius: 8 }}>
                <Skeleton active paragraph={{ rows: 6 }} />
              </Card>
            </>
          ) : error ? (
            <Alert
              type="error"
              message={error}
              showIcon
              style={{ marginBottom: 24 }}
              action={<Button size="small" onClick={() => setRetryCount(c => c + 1)}>Retry</Button>}
              onClose={() => setError(null)}
              closable
            />
          ) : renderContent()}
        </Content>
      </Layout>

      {/* ── New Ticket Modal ── */}
      <Modal title="Report an Issue" open={ticketModal}
        onOk={() => ticketForm.submit()}
        onCancel={() => { setTicketModal(false); ticketForm.resetFields(); }}
        okText="Submit" destroyOnHidden keyboard>
        <Form form={ticketForm} layout="vertical" onFinish={handleCreateTicket}>
          <Form.Item name="subject" label="Subject" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="Brief description of the issue" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{ required: true, message: "Select priority" }]}>
            <Select placeholder="Select priority">
              <Select.Option value="LOW">Low</Select.Option>
              <Select.Option value="MEDIUM">Medium</Select.Option>
              <Select.Option value="HIGH">High</Select.Option>
              <Select.Option value="CRITICAL">Critical</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true, message: "Required" }]}>
            <Input.TextArea rows={4} placeholder="Describe the issue in detail" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Ticket Detail Modal ── */}
      <Modal title="Ticket Details" open={Boolean(ticketDetail)}
        onCancel={() => setTicketDetail(null)}
        footer={<Button onClick={() => setTicketDetail(null)}>Close</Button>}
        destroyOnHidden keyboard width={500}>
        {ticketDetail && (
          <div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {[
                  ["Subject",  ticketDetail.subject],
                  ["Priority", <Tag color={{ LOW: "default", MEDIUM: "blue", HIGH: "orange", CRITICAL: "red" }[ticketDetail.priority]}>{ticketDetail.priority}</Tag>],
                  ["Status",   <Tag color={{ OPEN: "blue", IN_PROGRESS: "orange", RESOLVED: "green", CLOSED: "default" }[ticketDetail.status]}>{ticketDetail.status?.replace(/_/g, " ")}</Tag>],
                  ["Created",  new Date(ticketDetail.created_at).toLocaleString()],
                  ["Updated",  new Date(ticketDetail.updated_at).toLocaleString()],
                ].map(([label, val]) => (
                  <tr key={label} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "8px 12px", color: "#8c8c8c", width: "35%" }}>{label}</td>
                    <td style={{ padding: "8px 12px" }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 12 }}>
              <Text strong>Description</Text>
              <div style={{ marginTop: 6, padding: 10, background: "#fafafa", borderRadius: 4, fontSize: 13, color: "#595959" }}>
                {ticketDetail.description}
              </div>
            </div>
            {ticketDetail.resolution_notes && (
              <div style={{ marginTop: 12 }}>
                <Text strong>Resolution Notes</Text>
                <div style={{ marginTop: 6, padding: 10, background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4, fontSize: 13 }}>
                  {ticketDetail.resolution_notes}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
}
